<template>
  <header
    class="sticky top-0 bg-white dark:!bg-background z-10 flex h-12 w-full shrink-0 items-center border-b justify-between gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-10 scrollbar-hidden"
  >
    <div class="flex items-center justify-between w-full">
      <div class="flex items-center gap-2 px-4">
        <div class="flex-1 min-w-0 overflow-x-auto whitespace-nowrap scrollbar-hidden">
          <Breadcrumb>
            <BreadcrumbList class="flex flex-nowrap">
              <template v-for="(crumb, index) in breadcrumbItems" :key="index">
                <BreadcrumbItem class="flex-shrink-0">
                  <RouterLink v-if="index !== breadcrumbItems.length - 1" :to="crumb.to">
                    <BreadcrumbPage class="text-muted-foreground">
                      {{ crumb.label }}
                    </BreadcrumbPage>
                  </RouterLink>
                  <BreadcrumbPage v-else>
                    {{ crumb.label }}
                  </BreadcrumbPage>
                </BreadcrumbItem>
                <BreadcrumbSeparator v-if="index !== breadcrumbItems.length - 1" class="[&>svg]:w-6 [&>svg]:h-12" />
              </template>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>
      <div class="flex items-center gap-2 pr-2 mb-0.5">
        <ThemeToggle />
        <Button
          id="feedback-button"
          variant="outline"
          size="sm"
          class="h-7 px-3 flex items-center gap-1 bg-slate-100 text-[#074880] dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 rounded-2xl"
        >
          <ThumbsUp class="w-5 h-5" />
          Feedback
        </Button>
        <DropdownMenu v-if="isClientUser">
          <DropdownMenuTrigger as-child class="cursor-pointer">
            <Avatar class="h-8 w-8 rounded-2xl mt-1">
              <AvatarImage class="!overflow-hidden rounded-2xl" :src="user.avatar" :alt="user.username" />
              <AvatarFallback class="rounded-2xl"> {{ user.usernameInitials }} </AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            class="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side="bottom"
            align="end"
            :side-offset="4"
          >
            <DropdownMenuLabel class="p-0 font-normal">
              <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar class="h-8 w-8 rounded-lg">
                  <AvatarImage :src="user.avatar ?? ''" :alt="user.username" />
                  <AvatarFallback class="rounded-lg"> {{ user.usernameInitials }} </AvatarFallback>
                </Avatar>
                <div class="grid flex-1 text-left text-sm leading-tight">
                  <span class="truncate font-semibold">{{ user.username }}</span>
                  <span class="truncate text-xs">{{ user.email }}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuItem class="cursor-pointer flex items-center gap-2" @click="$router.push({ name: 'Tickets' })">
              <StickyNote class="w-5 h-5" />
              <span>Tickets</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              class="cursor-pointer flex items-center gap-2"
              @click="$router.push({ name: 'Clients Insights' })"
            >
              <ChartPie class="w-5 h-5" />
              <span>Insights</span>
            </DropdownMenuItem>
            <DropdownMenuGroup> </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem class="cursor-pointer" @click="logout">
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ChartPie, LogOut, StickyNote, ThumbsUp } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import ThemeToggle from '@/components/ThemeToggle.vue'
import { useAuth } from '@/composables'
import { Avatar, AvatarFallback, AvatarImage } from '@/foundation/avatar'
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/foundation/breadcrumb'
import { Button } from '@/foundation/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/foundation/dropdown-menu'
import { useAccountStore } from '@/stores/account'
import { useQueuesStore } from '@/stores/queues'

const { isClientUser } = useAccountStore()
const accountStore = useAccountStore()

const { logout } = useAuth()
const user = computed<{ username: string; email: string; avatar: string; usernameInitials: string }>(() => {
  const u = accountStore.user
  const rawName = u.name ?? ''
  const username = rawName.trim() !== '' ? rawName : (u.username ?? '')
  const email = u.email ?? ''
  const avatar = String(u.avatar ?? '')
  const initials = username.substring(0, 2).toUpperCase()

  return {
    username,
    email,
    avatar,
    usernameInitials: initials,
  }
})

const route = useRoute()
const { queues } = storeToRefs(useQueuesStore())

const breadcrumbItems = computed(() => {
  const items = route.matched.map((record) => {
    let label = ''

    if (record.meta && record.meta.breadcrumb) {
      label =
        typeof record.meta.breadcrumb === 'function' ? record.meta.breadcrumb(route, queues.value) : record.meta.breadcrumb
    } else {
      label = record.name ? record.name.toString() : ''
    }

    return {
      label,
      to: record.path,
    }
  })

  if (route.name === 'CustomerDetails') {
    return [{ label: 'Customers', to: '/customers' }, ...items]
  }

  return items
})
</script>

<style scoped>
.scrollbar-hidden::-webkit-scrollbar {
  display: none;
}
.scrollbar-hidden {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
</style>
